import { z } from 'zod';
import { isValidCPF, isValidPlate, normalizePlate } from './br.js';

export const latLngSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

/** Só dígitos, DDD + número. O cliente manda formatado; normalizamos aqui. */
export const phoneSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .pipe(z.string().regex(/^\d{10,11}$/, 'telefone inválido'));

export const passwordSchema = z
  .string()
  .min(8, 'senha precisa de no mínimo 8 caracteres')
  .max(200);

export const signupSchema = z.object({
  name: z.string().trim().min(3).max(120),
  email: z.string().trim().toLowerCase().email().max(190),
  phone: phoneSchema,
  password: passwordSchema,
  role: z.enum(['passenger', 'driver']),
});

/** Nome, e-mail e telefone — nunca CPF, que não muda por decisão do usuário. */
export const profileUpdateSchema = z.object({
  name: z.string().trim().min(3).max(120),
  email: z.string().trim().toLowerCase().email().max(190),
  phone: phoneSchema,
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(190),
  password: z.string().min(1).max(200),
  /** obrigatório apenas para admin */
  totp: z.string().regex(/^\d{6}$/).optional(),
});

export const vehicleTypeSchema = z.enum(['car', 'moto']);

export const createRideSchema = z.object({
  vehicleType: vehicleTypeSchema,
  origin: latLngSchema,
  originAddress: z.string().trim().min(3).max(255),
  destination: latLngSchema,
  destinationAddress: z.string().trim().min(3).max(255),
});

export const positionSchema = latLngSchema.extend({
  heading: z.number().min(0).max(360).optional(),
  accuracy: z.number().nonnegative().optional(),
});

export const ratingSchema = z.object({
  stars: z.number().int().min(1).max(5),
  tags: z.array(z.string().trim().min(1).max(30)).max(5).optional(),
  comment: z.string().trim().max(500).optional(),
});

/**
 * Como o motorista recebeu o valor em dinheiro.
 *
 * Pix e cartão passam por fora do sistema — a maquininha e a chave são
 * dele. O que se guarda aqui é só o registro do que aconteceu, para o
 * fechamento dele bater com a realidade.
 */
export const paymentMethodSchema = z.enum(['cash', 'pix', 'credit', 'debit']);

export const paymentSchema = z.object({ method: paymentMethodSchema });

export const cancelSchema = z.object({
  reason: z.string().trim().max(255).optional(),
});

/** ISO yyyy-mm-dd que não pode estar no passado. */
const futureDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'data deve ser yyyy-mm-dd')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    return d.getTime() >= today.getTime();
  }, 'documento vencido');

export const driverProfileSchema = z.object({
  cpf: z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .refine(isValidCPF, 'CPF inválido'),
  cnhNumber: z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .pipe(z.string().regex(/^\d{9,11}$/, 'número de CNH inválido')),
  cnhExpiresOn: futureDateSchema,
});

export const vehicleSchema = z.object({
  type: vehicleTypeSchema,
  plate: z.string().transform(normalizePlate).refine(isValidPlate, 'placa inválida'),
  model: z.string().trim().min(2).max(80),
  color: z.string().trim().min(3).max(30),
  year: z.coerce
    .number()
    .int()
    .min(1950)
    .max(new Date().getFullYear() + 1),
});

export const documentKindSchema = z.enum(['cnh', 'selfie', 'crlv']);

export const rejectSchema = z.object({
  reason: z.string().trim().min(5, 'diga o motivo da recusa').max(500),
});

/**
 * Lançamento manual na carteira. Teto de R$ 1.000 por lançamento: é
 * digitação humana em centavos, e um zero a mais vira prejuízo. Valor
 * maior é feito em lançamentos separados, cada um auditado.
 */
export const walletEntrySchema = z.object({
  amountCents: z
    .number()
    .int('valor deve ser em centavos inteiros')
    .refine((v) => v !== 0, 'valor não pode ser zero')
    .refine((v) => Math.abs(v) <= 100_000, 'valor acima do limite por lançamento (R$ 1.000)'),
  kind: z.enum(['refund', 'promo', 'adjustment']),
  description: z.string().trim().min(5, 'descreva o motivo').max(255),
});

export const planSchema = z.object({
  name: z.string().trim().min(2).max(80),
  days: z.coerce.number().int().min(1).max(3650),
  priceCents: z.coerce.number().int().min(0).max(1_000_000),
});

export const grantSubscriptionSchema = z.object({
  planId: z.coerce.number().int().positive(),
  startsOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'data deve ser yyyy-mm-dd')
    .optional(),
  pricePaidCents: z.coerce.number().int().min(0).max(1_000_000).optional(),
  note: z.string().trim().max(255).optional(),
});

/**
 * Nova versão de tabela de tarifa. Editar não altera a linha antiga: cria
 * outra e desativa a anterior, para que corrida já feita mantenha o preço
 * que tinha.
 */
export const fareTableSchema = z.object({
  vehicleType: vehicleTypeSchema,
  baseCents: z.coerce.number().int().min(0).max(100_000),
  perKmCents: z.coerce.number().int().min(0).max(100_000),
  perMinCents: z.coerce.number().int().min(0).max(100_000),
  minFareCents: z.coerce.number().int().min(0).max(100_000),
});

export type FareTableInput = z.infer<typeof fareTableSchema>;

export type WalletEntry = z.infer<typeof walletEntrySchema>;
export type PlanInput = z.infer<typeof planSchema>;
export type GrantSubscription = z.infer<typeof grantSubscriptionSchema>;

export type DriverProfile = z.infer<typeof driverProfileSchema>;
export type VehicleInput = z.infer<typeof vehicleSchema>;
export type DocumentKind = z.infer<typeof documentKindSchema>;

export type ProfileUpdate = z.infer<typeof profileUpdateSchema>;
export type Signup = z.infer<typeof signupSchema>;
export type Login = z.infer<typeof loginSchema>;
export type CreateRide = z.infer<typeof createRideSchema>;
export type Position = z.infer<typeof positionSchema>;
export type RatingInput = z.infer<typeof ratingSchema>;
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

/** Cotação: só origem e destino, sem criar corrida. */
export const quoteSchema = z.object({
  origin: latLngSchema,
  destination: latLngSchema,
});
